<script lang="ts">
    import type {
        AudioEntry,
        SearchAudioCarrier
    } from "@app/models/api";
    import { glosbeUiServerUrlWithoutPath } from "@app/services/glosbe-ui-server-endpoints";
    import {
        AUDIO__recordings__title,
        COMMON__loading,
        COMMON__notFound,
        ERROR__error,
        SPEECH__tryAgain
    } from "@app/services/i18n.messages";
    import { getMessage } from "@app/services/i18n.service";
    import { slide } from "svelte/transition";
    import AudioListItem from "./AudioListItem.svelte";
    import {
    pauseAudioPlayback,
        playAudioFileByAudioEntry,

        toggleAudioPlayback

    } from "./AudioPlayerService";
    import { onDestroy } from "svelte";

    export let phrase: string;
    export let lang: string;

    export let open: boolean = false;
    export let autoplayFirstFile = false;
    export let autoplayFileId: string | undefined = undefined;

    export let onClose: () => void;
    let firstListItem: AudioListItem | undefined = undefined;

    export function playFirst() {
        if (firstListItem && typeof firstListItem.play === "function") {
            firstListItem.play();
        }
    }

    let containerElement: HTMLDivElement;
    export let activatorElement: HTMLElement;
    let audiosListContainer: HTMLDivElement;

    let audios: AudioEntry[] | undefined;

    async function doLoadAudiosWithPromise(
        resolve: (audioCarrier: SearchAudioCarrier) => void,
        reject: any,
    ) {
        try {
            const url = `${glosbeUiServerUrlWithoutPath}/api/audios/${encodeURIComponent(
                lang,
            )}/${encodeURIComponent(phrase)}`;
            const audiosResp = await fetch(url);
            const audiosJson = (await audiosResp.json()) as SearchAudioCarrier;
            if (autoplayFileId) {
                const entries = audiosJson.phraseAudioCarrier.audioEntries;

                if (entries) {
                    entries.sort((entryA, entryB) => {
                        if (entryA.fbFileId === autoplayFileId) {
                            return -1;
                        }
                        if (entryB.fbFileId === autoplayFileId) {
                            return 1;
                        }
                        return 0;
                    });
                }
            }
            resolve(audiosJson);
        } catch (e) {
            reject(e);
        }
    }

    let loadAudiosPromise: Promise<SearchAudioCarrier> =
        new Promise<SearchAudioCarrier>(doLoadAudiosWithPromise);
    loadAudiosPromise.then((audiosFromApi) => {
        audios = audiosFromApi.phraseAudioCarrier.audioEntries;
    });

    function loadAudios() {
        setTimeout(() => {
            loadAudiosPromise = new Promise<SearchAudioCarrier>(
                doLoadAudiosWithPromise,
            );
        }, 1);
    }

    function closeOnOutsideClick(event: Event) {
        const clickOutsideContainer =
            containerElement &&
            !containerElement.contains(event.target as HTMLElement);
        const clickOutsideActivator =
            activatorElement &&
            !activatorElement.contains(event.target as HTMLElement);

        if (open && clickOutsideActivator && clickOutsideContainer) {
            onClose();
        }
    }

    if (autoplayFirstFile) {
        loadAudiosPromise.then((result) => {
            if (
                result.phraseAudioCarrier.audioEntries &&
                result.phraseAudioCarrier.audioEntries.length > 0
            ) {
                const entryToPlay = result.phraseAudioCarrier.audioEntries[0];
                playAudioFileByAudioEntry(
                    result.phraseAudioCarrier.audioEntries[0],
                );
                activatorElement.dataset["file"] = entryToPlay.url.mp3;
                activatorElement.dataset["fileId"] = entryToPlay.fbFileId;
            }
        });
    }

    function fixAudiosListPosition() {
        setTimeout(() => {
            const box = audiosListContainer.getBoundingClientRect();
            if (document.body.clientWidth < box.x + box.width) {
                audiosListContainer.style.left = `${
                    Math.min(document.body.clientWidth - box.x - box.width - 16, 8)
                }px`;
            }
        }, 0);
    }

    loadAudiosPromise.then(fixAudiosListPosition).catch(fixAudiosListPosition);
    $: {
        if (audiosListContainer) {
            fixAudiosListPosition();
        }
    }
    onDestroy(() => {
        pauseAudioPlayback()
    })
</script>

<svelte:window on:click={closeOnOutsideClick} />
{#if open}
    <div class="z-0" bind:this={containerElement}>
        <div
            class="absolute w-full mt-4 left-2 right-2 md:right-auto md:left-auto max-w-96 md:w-96
      md:bottom-auto max-h-screen bg-white shadow-md rounded
      border z-10 audiosList border-gray-400"
            data-testid="audios-list"
            transition:slide
            bind:this={audiosListContainer}
        >
            <div class="bg-gray-100 text-gray-700 px-2 h-10 border-b">
                {getMessage(AUDIO__recordings__title)}
                <button
                    data-testid="audios-list-close"
                    on:click={onClose}
                    class="float-right"
                    ><span class="icon-close" />
                </button>
            </div>
            {#await loadAudiosPromise}
                <p class="p-2 text-sm text-gray-700 text-center">
                    {getMessage(COMMON__loading)}
                </p>
            {:then audios}
                <ul
                    class="overflow-y-scroll h-full bg-white text-sm text-gray-700"
                    transition:slide
                >
                    {#if audios.phraseAudioCarrier && audios.phraseAudioCarrier.audioEntries}
                        {#each audios.phraseAudioCarrier.audioEntries as audio, index}
                            <li data-testid="audios-list-item" class="border-b">
                                {#if index === 0}
                                    <AudioListItem
                                        {audio}
                                        bind:this={firstListItem}
                                    />
                                {:else}
                                    <AudioListItem {audio} />
                                {/if}
                            </li>
                        {/each}
                    {:else}
                        {getMessage(COMMON__notFound)}
                    {/if}
                </ul>
            {:catch e}
                <p class="p-2 text-sm text-gray-700">
                    {getMessage(ERROR__error)}
                    <button on:click={loadAudios} class="m-auto text-button-xs">
                        {getMessage(SPEECH__tryAgain)}
                    </button>
                </p>
            {/await}
        </div>
    </div>
{/if}

<style>
    .audiosList {
        max-height: 50vh;
        overflow-y: hidden;
    }
    .audiosList ul {
        max-height: calc(50vh - 2rem);
    }
</style>
